import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCustomerById } from '../api';

const navLinkStyle: React.CSSProperties = {
  display: 'block',
  padding: '1rem 1.5rem',
  color: '#fff',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 17,
};

// Define an interface for expandable menu items
interface ExpandableMenuItem {
  id: string;
  isExpanded: boolean;
}

const Navbar: React.FC = () => {
  const { user, logout, setCustomerName } = useAuth();
  const [expandableMenus, setExpandableMenus] = useState<ExpandableMenuItem[]>([
    { id: 'development', isExpanded: false },
    { id: 'sales', isExpanded: false },
    { id: 'talent', isExpanded: false },
    { id: 'support', isExpanded: false },
    // Add more expandable menus here as needed
  ]);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    // Fetch customer information when user is available and we don't already have the customer name
    if (user?.customerId && !user?.customerName) {
      fetchCustomerInfo(user.customerId);
    }
  }, [user?.customerId, user?.customerName]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 1024;
      setIsMobile(newIsMobile);

      // Close mobile menu when switching to desktop
      if (!newIsMobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Use a dynamic viewport height to avoid 100vh issues on mobile browsers
  useEffect(() => {
    const setVhVar = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVhVar();
    window.addEventListener('resize', setVhVar);
    window.addEventListener('orientationchange', setVhVar);
    return () => {
      window.removeEventListener('resize', setVhVar);
      window.removeEventListener('orientationchange', setVhVar);
    };
  }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
      if (isMobile && mobileMenuOpen) {
        // Store current scroll position
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        
        return () => {
          // Restore scroll position when menu closes
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.style.overflow = '';
          window.scrollTo(0, scrollY);
        };
      }
    }, [isMobile, mobileMenuOpen]);
  

  // Auto-expand relevant section based on current path
  useEffect(() => {
    const newExpandableMenus = [...expandableMenus];
    let shouldUpdate = false;

    // Handle Development menu expansion
    if (!location.pathname.startsWith('/development')) {
      // Collapse development menu if we're not on a development page
      const developmentMenu = newExpandableMenus.find(menu => menu.id === 'development');
      if (developmentMenu && developmentMenu.isExpanded) {
        developmentMenu.isExpanded = false;
        shouldUpdate = true;
      }
    } else {
      // We're on a development page, expand the menu
      const developmentMenu = newExpandableMenus.find(menu => menu.id === 'development');
      if (developmentMenu && !developmentMenu.isExpanded) {
        developmentMenu.isExpanded = true;
        shouldUpdate = true;
      }
    }

    // Handle Support menu expansion
    if (!location.pathname.startsWith('/support')) {
      // Collapse support menu if we're not on a support page
      const supportMenu = newExpandableMenus.find(menu => menu.id === 'support');
      if (supportMenu && supportMenu.isExpanded) {
        supportMenu.isExpanded = false;
        shouldUpdate = true;
      }
    } else {
      // We're on a support page, expand the menu
      const supportMenu = newExpandableMenus.find(menu => menu.id === 'support');
      if (supportMenu && !supportMenu.isExpanded) {
        supportMenu.isExpanded = true;
        shouldUpdate = true;
      }
    }

    // Add more path checks for other expandable menus here (sales, talent)

    if (shouldUpdate) {
      setExpandableMenus(newExpandableMenus);
    }
  }, [location.pathname]);

  // Handle clicks outside of the user menu and mobile menu to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close user menu if clicking outside
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }

      // Close mobile menu if clicking outside on mobile
      if (isMobile && mobileMenuOpen && navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, mobileMenuOpen]);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, mobileMenuOpen]);

  const fetchCustomerInfo = async (customerId: number) => {
    try {
      const response = await getCustomerById(customerId);
      if (response.customer && response.customer.Name) {
        setCustomerName(response.customer.Name);
      }
    } catch (error) {
      console.error('Error fetching customer info:', error);
    }
  };

  const handleLogout = () => {
    logout();
    // Redirect will be handled by ProtectedRoute component
  };

  // Toggle a specific menu and collapse others
  const toggleMenu = (menuId: string) => {
    const updatedMenus = expandableMenus.map(menu => {
      if (menu.id === menuId) {
        // Toggle the clicked menu
        return { ...menu, isExpanded: !menu.isExpanded };
      } else {
        // Collapse all other menus
        return { ...menu, isExpanded: false };
      }
    });

    setExpandableMenus(updatedMenus);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Handle navigation link clicks in mobile
  const handleMobileNavClick = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
      setUserMenuOpen(false);
      // Collapse all expandable menus
      const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
      setExpandableMenus(updatedMenus);
    }
  };

  // Helper function to check if a menu is expanded
  const isMenuExpanded = (menuId: string): boolean => {
    const menu = expandableMenus.find(m => m.id === menuId);
    return menu ? menu.isExpanded : false;
  };

  return (
    <>
      {/* Mobile hamburger menu button (hidden when menu is open) */}
      {isMobile && !mobileMenuOpen && (
        <button
          onClick={toggleMobileMenu}
          style={{
            position: 'fixed',
            top: '16px',
            left: '16px',
            zIndex: 1002,
            background: '#222b45',
            border: 'none',
            borderRadius: '6px',
            padding: '12px',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          {/* Hamburger icon */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      )}

      {/* Mobile overlay */}
      {isMobile && mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.2s ease-in-out'
          }}
        />
      )}

      {/* Main navigation */}
      <nav
        ref={navbarRef}
        style={{
          width: isMobile ? (mobileMenuOpen ? '280px' : '0') : '220px',
          background: '#222b45',
          color: '#fff',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(var(--vh, 1vh) * 100)',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: isMobile ? 1001 : 1000,
          transform: isMobile ? (mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
          transition: isMobile ? 'transform 0.3s ease-in-out' : 'none',
          overflow: 'hidden'
        }}
      >
        {/* Header - Fixed at top */}
        <div style={{ position: 'relative', padding: '1rem 1rem 0.5rem', fontWeight: 700, fontSize: 22, borderBottom: '1px solid #333', textAlign: 'center' }}>
          <img src="/execdash_logo_trans.png" alt="execdash logo" style={{ width: 100, height: 'auto', marginTop: 10 }} />
          {/* Explicit close button inside panel for mobile */}
          {isMobile && mobileMenuOpen && (
            <button
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'transparent',
                border: 'none',
                color: '#8f9bb3',
                cursor: 'pointer',
                padding: 8,
                borderRadius: 4
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
          {user?.customerName && (
            <div style={{
              fontSize: 14,
              fontWeight: 400,
              color: '#8f9bb3',
              marginTop: '0.25rem',
              marginBottom: '0.75rem'
            }}>
              {user.customerName}
            </div>
          )}
          {user && (
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <div
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                style={{
                  fontSize: 16,
                  marginBottom: '1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: userMenuOpen ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  overflowWrap: 'anywhere'
                }}
              >
                <span>{user.firstName ? `${user.firstName} ${user.lastName || ''}` : user.email}</span>
                <span style={{ marginLeft: '6px', fontSize: 10 }}>{userMenuOpen ? '▲' : '▼'}</span>
              </div>

              {userMenuOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '90%',
                  background: '#2d3a5d',
                  borderRadius: '4px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                  zIndex: 1001
                }}>
                  <button
                    onClick={() => {
                      handleLogout();
                      setUserMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'transparent',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: 14,
                      textAlign: 'center'
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Navigation - Scrollable */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li>
              <NavLink
                to="/overview"
                className={({ isActive }) => isActive ? 'nav-active' : ''}
                style={navLinkStyle}
                onClick={() => {
                  handleMobileNavClick();
                  // Collapse all expandable menus when clicking on a regular menu item
                  const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
                  setExpandableMenus(updatedMenus);
                }}
              >
                Overview
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/market-insights"
                className={({ isActive }) => isActive ? 'nav-active' : ''}
                style={navLinkStyle}
                onClick={() => {
                  handleMobileNavClick();
                  // Collapse all expandable menus when clicking on a regular menu item
                  const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
                  setExpandableMenus(updatedMenus);
                }}
              >
                Market Insights
              </NavLink>
            </li>
            <li>
              <div
                onClick={() => toggleMenu('development')}
                style={{
                  ...navLinkStyle,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: location.pathname.startsWith('/development') ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
                }}
              >
                <span>Development</span>
                <span style={{ fontSize: 12 }}>{isMenuExpanded('development') ? '▼' : '▶'}</span>
              </div>
              {isMenuExpanded('development') && (
                <div style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                  <NavLink
                    to="/development/overview"
                    className={({ isActive }) => isActive ? 'nav-active' : ''}
                    style={({ isActive }) => ({
                      ...navLinkStyle,
                      fontSize: 14,
                      padding: '0.5rem 1.5rem 0.5rem 2.5rem',
                      color: isActive ? '#fff' : '#8f9bb3',
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center'
                    })}
                    onClick={handleMobileNavClick}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ marginRight: '8px', fontSize: '10px' }}>●</span>}
                        Overview
                      </>
                    )}
                  </NavLink>
                  <NavLink
                    to="/development/iterations"
                    className={({ isActive }) => isActive ? 'nav-active' : ''}
                    style={({ isActive }) => ({
                      ...navLinkStyle,
                      fontSize: 14,
                      padding: '0.5rem 1.5rem 0.5rem 2.5rem',
                      color: isActive ? '#fff' : '#8f9bb3',
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center'
                    })}
                    onClick={handleMobileNavClick}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ marginRight: '8px', fontSize: '10px' }}>●</span>}
                        Iterations
                      </>
                    )}
                  </NavLink>
                  <NavLink
                    to="/development/developers"
                    className={({ isActive }) => isActive ? 'nav-active' : ''}
                    style={({ isActive }) => ({
                      ...navLinkStyle,
                      fontSize: 14,
                      padding: '0.5rem 1.5rem 0.5rem 2.5rem',
                      color: isActive ? '#fff' : '#8f9bb3',
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center'
                    })}
                    onClick={handleMobileNavClick}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ marginRight: '8px', fontSize: '10px' }}>●</span>}
                        Developers
                      </>
                    )}
                  </NavLink>
                </div>
              )}
            </li>
            <li>
              <div
                onClick={() => toggleMenu('support')}
                style={{
                  ...navLinkStyle,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: location.pathname.startsWith('/support') ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
                }}
              >
                <span>Support</span>
                <span style={{ fontSize: 12 }}>{isMenuExpanded('support') ? '▼' : '▶'}</span>
              </div>
              {isMenuExpanded('support') && (
                <div style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                  <NavLink
                    to="/support/overview"
                    className={({ isActive }) => isActive ? 'nav-active' : ''}
                    style={({ isActive }) => ({
                      ...navLinkStyle,
                      fontSize: 14,
                      padding: '0.5rem 1.5rem 0.5rem 2.5rem',
                      color: isActive ? '#fff' : '#8f9bb3',
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center'
                    })}
                    onClick={handleMobileNavClick}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ marginRight: '8px', fontSize: '10px' }}>●</span>}
                        Overview
                      </>
                    )}
                  </NavLink>
                  <NavLink
                    to="/support/customers"
                    className={({ isActive }) => isActive ? 'nav-active' : ''}
                    style={({ isActive }) => ({
                      ...navLinkStyle,
                      fontSize: 14,
                      padding: '0.5rem 1.5rem 0.5rem 2.5rem',
                      color: isActive ? '#fff' : '#8f9bb3',
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center'
                    })}
                    onClick={handleMobileNavClick}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ marginRight: '8px', fontSize: '10px' }}>●</span>}
                        Customers
                      </>
                    )}
                  </NavLink>
                  <NavLink
                    to="/support/team-members"
                    className={({ isActive }) => isActive ? 'nav-active' : ''}
                    style={({ isActive }) => ({
                      ...navLinkStyle,
                      fontSize: 14,
                      padding: '0.5rem 1.5rem 0.5rem 2.5rem',
                      color: isActive ? '#fff' : '#8f9bb3',
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center'
                    })}
                    onClick={handleMobileNavClick}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ marginRight: '8px', fontSize: '10px' }}>●</span>}
                        Team Members
                      </>
                    )}
                  </NavLink>
                </div>
              )}
            </li>

            {/* <li>
          <NavLink 
            to="/finance" 
            className={({ isActive }) => isActive ? 'nav-active' : ''} 
            style={{ ...navLinkStyle, pointerEvents: 'none', color: '#666' }}
            onClick={() => {
              // Collapse all expandable menus when clicking on a regular menu item
              const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
              setExpandableMenus(updatedMenus);
            }}
          >
            Finance
          </NavLink>
        </li>
        <li>
          <NavLink 
            to="/talent" 
            className={({ isActive }) => isActive ? 'nav-active' : ''} 
            style={{ ...navLinkStyle, pointerEvents: 'none', color: '#666' }}
            onClick={() => {
              // Collapse all expandable menus when clicking on a regular menu item
              const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
              setExpandableMenus(updatedMenus);
            }}
          >
            Talent
          </NavLink>
        </li>
        <li>
          <NavLink 
            to="/sales" 
            className={({ isActive }) => isActive ? 'nav-active' : ''} 
            style={{ ...navLinkStyle, pointerEvents: 'none', color: '#666' }}
            onClick={() => {
              // Collapse all expandable menus when clicking on a regular menu item
              const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
              setExpandableMenus(updatedMenus);
            }}
          >
            Sales
          </NavLink>
        </li> */}


          </ul>
        </div>
        {/* Footer - Fixed at bottom */}
        <div style={{ borderTop: '1px solid #333', padding: '1rem', marginTop: 'auto' }}>
          <NavLink
            to="/settings"
            className={({ isActive }) => isActive ? 'nav-active' : ''}
            style={({ isActive }) => ({
              display: 'block',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: isActive ? '#fff' : '#fff',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: 16,
              background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              borderRadius: '4px',
              textAlign: 'center'
            })}
            onClick={() => {
              handleMobileNavClick();
              // Collapse all expandable menus when clicking on settings
              const updatedMenus = expandableMenus.map(menu => ({ ...menu, isExpanded: false }));
              setExpandableMenus(updatedMenus);
            }}
          >
            Configure
          </NavLink>

        </div>
      </nav>
    </>
  );
};

export default Navbar;
